import { useEffect, useRef, useState } from "react";
import { CaretDown, SignOut, User, Palette, IdentificationCard, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import AboutDialog from "@/components/AboutDialog";

/**
 * UserProfileMenu — V1.1.1 OP-005 · header profile dropdown.
 *
 * Replaces the inline "name · role · sign-out" strip with a real menu:
 *   Name (display)
 *   Email (mono, muted)
 *   Role
 *   Department (from user.department_roles — future-ready, "Not set" today)
 *   Theme        → placeholder button (V1.1.1 defers the theme picker)
 *   Sign Out
 *
 * The user avatar is initials-only (no photo integration in V1.1.1) —
 * keeps the UI dependency-free and privacy-friendly.
 */
export default function UserProfileMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;

  const department =
    Array.isArray(user.department_roles) && user.department_roles.length
      ? user.department_roles.join(", ")
      : null;
  const initials = (user.full_name || "?")
    .split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="user-profile-menu-button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md pl-1 pr-2 py-1.5 hover:bg-surface-alt"
      >
        <span
          data-testid="user-profile-avatar"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-charcoal text-xs font-semibold text-ink-inverse"
        >
          {initials}
        </span>
        <span className="hidden text-left sm:block">
          <span data-testid="user-profile-name" className="block text-sm font-semibold text-ink-primary">
            {user.full_name}
          </span>
          <span data-testid="user-profile-role" className="block text-[11px] text-ink-secondary">
            {user.role}
          </span>
        </span>
        <CaretDown size={12} weight="bold" className="text-ink-secondary" />
      </button>

      {open && (
        <div
          data-testid="user-profile-menu"
          className="absolute right-0 top-12 z-40 w-72 overflow-hidden rounded-md border border-divider bg-surface shadow-lg"
        >
          <div className="border-b border-divider p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-charcoal text-sm font-semibold text-ink-inverse">
                {initials}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-ink-primary">{user.full_name}</div>
                <div className="truncate font-mono text-[11px] text-ink-secondary">{user.email}</div>
              </div>
            </div>
            <dl className="mt-3 space-y-1 text-[11px]">
              <ProfileRow icon={<IdentificationCard size={12} weight="bold" />} label="Role" value={user.role} />
              <ProfileRow
                icon={<User size={12} weight="bold" />}
                label="Department"
                value={department || <span className="text-ink-secondary">Not set</span>}
              />
            </dl>
          </div>

          <button
            data-testid="user-profile-theme"
            onClick={() => toast.info("Theme picker is coming in a future update.")}
            className="flex w-full items-center gap-2 border-b border-divider px-4 py-2.5 text-left text-sm text-ink-primary hover:bg-surface-alt"
          >
            <Palette size={14} weight="regular" />
            <span className="flex-1">Theme</span>
            <span className="text-[10px] uppercase tracking-wider text-ink-secondary">Soon</span>
          </button>

          <button
            data-testid="user-profile-about"
            onClick={() => { setOpen(false); setAboutOpen(true); }}
            className="flex w-full items-center gap-2 border-b border-divider px-4 py-2.5 text-left text-sm text-ink-primary hover:bg-surface-alt"
          >
            <Info size={14} weight="regular" />
            <span className="flex-1">About Coolman Operations</span>
          </button>

          <button
            data-testid="user-profile-signout"
            onClick={() => { setOpen(false); signOut(); }}
            className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-ink-primary hover:bg-surface-alt"
          >
            <SignOut size={14} weight="regular" />
            Sign Out
          </button>
        </div>
      )}
      <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />
    </div>
  );
}

function ProfileRow({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="flex items-center gap-1 text-ink-secondary">
        {icon} {label}
      </dt>
      <dd className="text-right text-ink-primary">{value || "—"}</dd>
    </div>
  );
}
